import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  LayoutGrid,
  List as ListIcon,
  Clock,
  MapPin,
  Users,
  Tag,
  AlertCircle,
  X,
  Filter,
  Search,
  CheckCircle2,
  ExternalLink,
  Shield,
  User,
  BarChart3
} from 'lucide-react';
import CalendarSkeleton from './CalendarSkeleton';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const EVENT_TYPES = [
  { id: 'task', label: 'Görev', color: 'purple' },
  { id: 'meeting', label: 'Toplantı', color: 'blue' },
  { id: 'client', label: 'Müşteri', color: 'green' },
  { id: 'deadline', label: 'Son Tarih', color: 'red' },
  { id: 'other', label: 'Diğer', color: 'gray' },
];

// Örnek olaylar yerine gerçek verileri kullanacağız

const Calendar = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [events, setEvents] = useState([]);
  const [taskRanges, setTaskRanges] = useState([]);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false);
  // Tarih sıralama state'i
  const [sortByDate, setSortByDate] = useState('default'); // 'default', 'oldest', 'newest'
  
  // Day details modal state
  const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
  const [selectedDayForDetails, setSelectedDayForDetails] = useState(null);
  const [selectedDayDetailEvents, setSelectedDayDetailEvents] = useState([]);
  const [showWeeklyDetailModal, setShowWeeklyDetailModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  
  // Yeni state: Günlük detay modalı için
  const [showDayDetailInAnalysis, setShowDayDetailInAnalysis] = useState(false);
  const [selectedAnalysisDay, setSelectedAnalysisDay] = useState(null);
  const [selectedAnalysisDayTasks, setSelectedAnalysisDayTasks] = useState({
    starting: [],
    ending: [],
    ongoing: []
  });

  useEffect(() => {
    fetchTasks();
  }, []);
  
  useEffect(() => {
    let count = 0;
    if (priorityFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    if (dateRangeFilter.start || dateRangeFilter.end) count++;
    if (searchQuery) count++;
    if (sortByDate !== 'default') count++;  // Sıralama filtresi için sayacı güncelle
    
    setActiveFiltersCount(count);
  }, [priorityFilter, statusFilter, dateRangeFilter, searchQuery, sortByDate]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data: projects, error } = await supabase
          .from('projects')
          .select('id, name')
          .order('name');

        if (error) throw error;
        
        const { data: consultings, error: consultingError } = await supabase
          .from('consulting')
          .select('id, name');
          
        if (consultingError) throw consultingError;
        
        // Tüm projeleri birleştir
        const allProjects = [
          ...(projects || []).map(p => ({ id: p.id, name: p.name, type: 'project' })),
          ...(consultings || []).map(c => ({ id: c.id, name: c.name, type: 'consulting' }))
        ];
        
        setAvailableProjects(allProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    
    fetchProjects();
  }, []);

  // Fetch current user and role
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setCurrentUser(userProfile);
        setUserRole(userProfile?.role);
        if (userProfile?.role !== 'super') {
          setShowOnlyMyTasks(true);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Permission-based event filtering
  const permissionFilteredEvents = useMemo(() => {
    return events.filter(event => {
      if (!currentUser || !userRole) return false;
      if (userRole === 'super' && !showOnlyMyTasks) return true;
      if (userRole === 'super' && showOnlyMyTasks) {
        // Only show events where user is assigned
        return event.assigned_members && event.assigned_members.some(m => m.id === currentUser.id);
      }
      if (userRole === 'admin') {
        // Admins see events they created or are assigned to
        return (event.created_by === currentUser.id) || (event.assigned_members && event.assigned_members.some(m => m.id === currentUser.id));
      }
      // Regular users see only assigned events
      return event.assigned_members && event.assigned_members.some(m => m.id === currentUser.id);
    });
  }, [events, currentUser, userRole, showOnlyMyTasks]);

  useEffect(() => {
    // Arama sonuçlarını güncelle (yetkiyle filtrelenmiş eventler)
    if (searchQuery.trim().length > 0) {
      const results = permissionFilteredEvents.filter(event => 
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery, permissionFilteredEvents]);

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const { data: tasks, error } = await supabase
        .from('tasks')
        .select(`
          id,
          title,
          description,
          start_date,
          end_date,
          priority,
          status,
          title,
          project:projects(id, name),
          sub_project:sub_projects(id, name),
          task_categories(
            category:categories(id, name, color)
          ),
          consulting_project:consulting_sub_projects(
            id,
            name,
            parent_consulting:consulting(id, name)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Görevleri olaylara dönüştür
      const taskEvents = tasks.map(task => {
        // Kategorileri düzenle
        const categories = task.task_categories.map(tc => tc.category);
        
        // Konsültan projesi için özel işleme
        let project = task.project;
        let subProject = task.sub_project;
        
        if (task.consulting_project) {
          project = {
            id: task.consulting_project.parent_consulting.id,
            name: task.consulting_project.parent_consulting.name,
            is_consulting: true,
            type: 'consulting'
          };
          subProject = {
            id: task.consulting_project.id,
            name: task.consulting_project.name,
            is_consulting: true
          };
        } else if (project) {
          // Normal proje tipini belirle
          project = {
            ...project,
            is_consulting: false,
            type: 'project'
          };
        }

        return {
          id: task.id,
          title: task.title,
          description: task.description,
          start: new Date(task.start_date),
          end: new Date(task.end_date),
          type: 'task',
          status: task.status,
          priority: task.priority,
          title: task.title,
          project: project,
          subProject: subProject,
          categories: categories
        };
      });

      setEvents(taskEvents);
      
      // Tarih aralıklarını oluştur
      const ranges = tasks.map(task => ({
        id: task.id,
        startDate: new Date(task.start_date),
        endDate: new Date(task.end_date),
        priority: task.priority
      }));
      
      setTaskRanges(ranges);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Görevler yüklenirken bir hata oluştu');
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const firstDayOfWeek = firstDay.getDay() || 7;
    
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const prevDate = new Date(year, month, 1 - i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({ date: nextDate, isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    // Belirli bir tarih için olayları getir (görevler)
    let filteredEvents = permissionFilteredEvents.filter(event => 
      event.start.getDate() === date.getDate() &&
      event.start.getMonth() === date.getMonth() &&
      event.start.getFullYear() === date.getFullYear()
    );
    // Filtreleme işlemlerini burada uygula
    filteredEvents = filterEvents(filteredEvents);
    return filteredEvents;
  };
  
  // Tüm filtreleme mantığını tek fonksiyona topla
  const filterEvents = (eventsToFilter) => {
    let result = [...eventsToFilter];
    
    // Arama sorgusu filtresi
    if (searchQuery) {
      result = result.filter(event => 
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Öncelik filtresi
    if (priorityFilter !== 'all') {
      result = result.filter(event => event.priority === priorityFilter);
    }
    
    // Durum filtresi
    if (statusFilter !== 'all') {
      result = result.filter(event => event.status === statusFilter);
    }
    
    // Tarih aralığı filtresi
    if (dateRangeFilter.start || dateRangeFilter.end) {
      if (dateRangeFilter.start) {
        const startDate = new Date(dateRangeFilter.start);
        startDate.setHours(0, 0, 0, 0);
        result = result.filter(event => event.start >= startDate);
      }
      
      if (dateRangeFilter.end) {
        const endDate = new Date(dateRangeFilter.end);
        endDate.setHours(23, 59, 59, 999);
        result = result.filter(event => event.end <= endDate);
      }
    }
    
    // Tarihe göre sıralama
    if (sortByDate !== 'default') {
      result.sort((a, b) => {
        if (sortByDate === 'oldest') {
          return a.start - b.start; // Eskiden yeniye (artan) sırala
        } else if (sortByDate === 'newest') {
          return b.start - a.start; // Yeniden eskiye (azalan) sırala
        }
        return 0;
      });
    }
    
    return result;
  };

  // Tüm liste görünümündeki etkinlikleri filtrele
  const getFilteredEvents = () => {
    return filterEvents(permissionFilteredEvents);
  };
  
  // Tüm filtreleri sıfırla
  const resetFilters = () => {
    setPriorityFilter('all');
    setStatusFilter('all');
    setDateRangeFilter({ start: '', end: '' });
    setSearchQuery('');
    setSortByDate('default');
  };

  // Bir tarihin herhangi bir görev aralığı içinde olup olmadığını kontrol et
  const isDateInTaskRange = (date) => {
    return taskRanges.find(range => {
      // Tarih aralığı içinde mi kontrol et
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      
      const startDate = new Date(range.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(range.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      return currentDate >= startDate && currentDate <= endDate;
    });
  };

  // Tarihin başlangıç veya bitiş günü olup olmadığını kontrol et
  const getDateTaskType = (date) => {
    // Hangi görevlerin başlangıç veya bitiş günü olduğunu kontrol et
    const startingTasks = taskRanges.filter(range => {
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      
      const startDate = new Date(range.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      return currentDate.getTime() === startDate.getTime();
    });
    
    const endingTasks = taskRanges.filter(range => {
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(range.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      return currentDate.getTime() === endDate.getTime();
    });

    return {
      isStart: startingTasks.length > 0,
      isEnd: endingTasks.length > 0,
      startingTasks,
      endingTasks
    };
  };

  // Takvim günlerinde bitecek olan görevleri getir
  const getTasksEndingOnDate = (date) => {
    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);
    
    const endingTaskIds = taskRanges
      .filter(range => {
        const endDate = new Date(range.endDate);
        endDate.setHours(0, 0, 0, 0);
        return endDate.getTime() === currentDate.getTime();
      })
      .map(range => range.id);
    
    // Bu ID'lere sahip görevleri bul
    return events.filter(event => endingTaskIds.includes(event.id));
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  // Format duration to show days and hours
  const formatDuration = (milliseconds) => {
    const totalHours = Math.round(milliseconds / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    
    if (days > 0 && hours > 0) {
      return `${days} gün ${hours} saat`;
    } else if (days > 0) {
      return `${days} gün`;
    } else if (hours > 0) {
      return `${hours} saat`;
    } else {
      return '1 saatten az';
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // İlgili öncelik için renk belirle
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const handleShowMoreClick = (date, dayEvents) => {
    setSelectedDayDate(date);
    setSelectedDayEvents(dayEvents);
    setShowDayEventsModal(true);
  };

  // Göreve gitme fonksiyonu
  const navigateToTask = (taskId) => {
    navigate(`/tasks?task=${taskId}`);
    setShowEventModal(false);
  };

  // Görev seçme ve tarihini görüntüleme fonksiyonu
  const selectTaskAndNavigate = (event) => {
    setSelectedTask(event);
    navigate('/tasks');
  };

  // Handle day click for detailed view
  const handleDayClick = (date) => {
    // O gün başlayan görevleri al
    const dayEvents = getEventsForDate(date);
    
    // O gün biten görevleri al
    const endingTasks = getTasksEndingOnDate(date);
    
    // Başlayan ve biten görevleri birleştir (tekrarları kaldır)
    const allDayEvents = [...dayEvents];
    endingTasks.forEach(endingTask => {
      if (!allDayEvents.find(event => event.id === endingTask.id)) {
        allDayEvents.push(endingTask);
      }
    });
    
    const taskType = getDateTaskType(date);
    
    setSelectedDayForDetails(date);
    setSelectedDayDetailEvents(allDayEvents);
    setShowDayDetailsModal(true);
  };

  // Analiz modalında gün detayını gösteren fonksiyon
  const handleAnalysisDayClick = (dayData, startingTasks, endingTasks, ongoingTasks) => {
    setSelectedAnalysisDay(dayData);
    setSelectedAnalysisDayTasks({
      starting: startingTasks,
      ending: endingTasks,
      ongoing: ongoingTasks
    });
    setShowDayDetailInAnalysis(true);
  };

  const EventModal = () => (
    <AnimatePresence>
      {showEventModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowEventModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {selectedEvent && (
              <>
                <div className="relative">
                  {/* Renkli başlık bölümü */}
                  <div className={`p-6 bg-${getPriorityColor(selectedEvent.priority)}-50 dark:bg-${getPriorityColor(selectedEvent.priority)}-900/20 border-b border-gray-200 dark:border-gray-700`}>
                    <button
                      onClick={() => setShowEventModal(false)}
                      className="absolute top-4 right-4 p-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    
                    <div className="flex items-start">
                      {/* Tarih göstergesi */}
                      <div className="mr-4 flex-none flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-white dark:bg-gray-700 shadow-sm">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {selectedEvent.start.getDate()}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {MONTHS[selectedEvent.start.getMonth()].substring(0, 3)}
                        </span>
                      </div>
                      
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedEvent.title}
                      </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getPriorityColor(selectedEvent.priority)}-100 dark:bg-${getPriorityColor(selectedEvent.priority)}-900/30 text-${getPriorityColor(selectedEvent.priority)}-700 dark:text-${getPriorityColor(selectedEvent.priority)}-400`}>
                            {selectedEvent.priority === 'high' ? 'Yüksek Öncelik' : selectedEvent.priority === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                    </div>
                  </div>
                </div>
                    </div>
                  </div>

                  {/* İçerik bölümü - Dikeyde kaydırılabilir */}
                  <div className="max-h-[60vh] overflow-y-auto p-6 space-y-5">
                  {selectedEvent.description && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Açıklama
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedEvent.description}
                    </p>
                      </div>
                  )}

                  <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Zaman Bilgileri
                      </h4>
                      
                      <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-2 text-sm">
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <Clock className="w-4 h-4 mr-2" />
                          Başlangıç
                    </div>
                        <div className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedEvent.start)} {formatTime(selectedEvent.start)}
                        </div>
                        
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <Clock className="w-4 h-4 mr-2" />
                          Bitiş
                        </div>
                        <div className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedEvent.end)} {formatTime(selectedEvent.end)}
                        </div>

                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <Clock className="w-4 h-4 mr-2" />
                          Süre
                        </div>
                        <div className="text-gray-700 dark:text-gray-300">
                          {formatDuration(selectedEvent.end - selectedEvent.start)}
                        </div>

                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <Clock className="w-4 h-4 mr-2" />
                          Kalan Süre
                        </div>
                        <div className="text-gray-700 dark:text-gray-300">
                          {formatDuration(selectedEvent.end - new Date())}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Görev Bilgileri
                      </h4>
                      
                      <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-2 text-sm">
                        {selectedEvent.status && (
                          <>
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Durum
                            </div>
                            <div className="text-gray-700 dark:text-gray-300">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full 
                                ${selectedEvent.status === 'done' 
                                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                                  :selectedEvent.status === 'todo' 
                                  ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' 
                                  :
                                   selectedEvent.status === 'in_progress'
                                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                  : selectedEvent.status === 'pending'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                                  : selectedEvent.status === 'cancelled'
                                  ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                  : 'bg-gray-100 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
                                }`}>
                                {selectedEvent.status === 'done' ? 'Tamamlandı' : 
                                selectedEvent.status === 'todo' ? 'Yapılacak' : 
                                 selectedEvent.status === 'in_progress' ? 'Devam Ediyor' : 
                                 selectedEvent.status === 'pending' ? 'Beklemede' :
                                 selectedEvent.status === 'cancelled' ? 'İptal Edildi' : 
                                 selectedEvent.status}
                              </span>
                            </div>
                          </>
                        )}
                        
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Öncelik
                        </div>
                        <div className="text-gray-700 dark:text-gray-300">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${
                            getPriorityColor(selectedEvent.priority)
                          }-100 dark:bg-${getPriorityColor(selectedEvent.priority)}-900/20 text-${
                            getPriorityColor(selectedEvent.priority)
                          }-700 dark:text-${getPriorityColor(selectedEvent.priority)}-400`}>
                            {selectedEvent.priority === 'high' ? 'Yüksek Öncelik' : 
                             selectedEvent.priority === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                          </span>
                        </div>

                    {selectedEvent.location && (
                          <>
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <MapPin className="w-4 h-4 mr-2" />
                              Konum
                            </div>
                            <div className="text-gray-700 dark:text-gray-300">
                        {selectedEvent.location}
                      </div>
                          </>
                        )}
                        
                        {selectedEvent.project && (
                          <>
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <Tag className="w-4 h-4 mr-2" />
                              Proje
                      </div>
                            <div className="text-gray-700 dark:text-gray-300">
                              {selectedEvent.project.name}
                              {selectedEvent.subProject && ` / ${selectedEvent.subProject.name}`}
                            </div>
                          </>
                        )}

                        {selectedEvent.categories && selectedEvent.categories.length > 0 && (
                          <>
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <Tag className="w-4 h-4 mr-2" />
                              Kategoriler
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {selectedEvent.categories.map(category => (
                                <span 
                                  key={category.id}
                                  className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${category.color || 'gray'}-100 dark:bg-${category.color || 'gray'}-900/20 text-${category.color || 'gray'}-700 dark:text-${category.color || 'gray'}-400`}
                                >
                                  {category.name}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                  </div>

                    {/* İlerleme Durumu */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        İlerleme Durumu
                      </h4>
                      
                      <div>
                        {(() => {
                          const now = new Date();
                          const start = new Date(selectedEvent.start);
                          const end = new Date(selectedEvent.end);
                          
                          // Toplam süre (ms)
                          const totalDuration = end - start;
                          // Geçen süre (ms)
                          const elapsedTime = now - start;
                          // İlerleme yüzdesi (0-100 arasında sınırlandırılmış)
                          const progress = Math.max(0, Math.min(100, (elapsedTime / totalDuration) * 100));
                          
                          // Henüz başlamamış
                          if (now < start) {
                            return (
                              <div className="text-sm text-blue-600 dark:text-blue-400">
                                Görev henüz başlamadı
                              </div>
                            );
                          }
                          
                          // Süresi dolmuş
                          if (now > end) {
                            return (
                              <>
                                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className="h-full bg-red-500 rounded-full" style={{ width: '100%' }}></div>
                                </div>
                                <div className="text-sm text-red-600 dark:text-red-400 mt-1">
                                  Görev süresi doldu
                                </div>
                              </>
                            );
                          }
                          
                          // Devam ediyor
                          return (
                            <>
                              <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${
                                    progress < 30 ? 'bg-green-500' : 
                                    progress > 70 ? 'bg-red-500' : 'bg-yellow-500'
                                  } rounded-full`} 
                                  style={{ width: `${Math.round(progress)}%` }}
                                ></div>
                            </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                %{Math.round(progress)} tamamlandı
                          </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                </div>
                  
                  {/* Alt butonlar - Sticky pozisyonlama */}
                  <div className="sticky bottom-0 w-full px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-2"
                      onClick={() => navigateToTask(selectedEvent.id)}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Detayları İncele
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-2"
                      onClick={() => setShowEventModal(false)}
                    >
                      Tamam
                    </motion.button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const DayEventsModal = () => (
    <AnimatePresence>
      {showDayEventsModal && selectedDayDate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDayEventsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {selectedDayDate.getDate()} {MONTHS[selectedDayDate.getMonth()]} {selectedDayDate.getFullYear()}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {selectedDayEvents.length} etkinlik
                </p>
              </div>
                    <button
                onClick={() => setShowDayEventsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                <X className="w-5 h-5" />
                    </button>
                  </div>
            
            <div className="max-h-[60vh] overflow-y-auto p-6">
              <div className="space-y-4">
                {selectedDayEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer"
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowDayEventsModal(false);
                      setShowEventModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 flex-shrink-0 rounded-full bg-${getPriorityColor(event.priority)}-500`}></div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {event.title}
                          </h4>
                </div>
                        
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <Clock className="w-3.5 h-3.5 mr-1.5" />
                          {formatTime(event.start)} - {formatTime(event.end)}
                        </div>
                        
                        {event.project && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <Tag className="w-3.5 h-3.5 mr-1.5" />
                            {event.project.name}
                            {event.subProject && ` / ${event.subProject.name}`}
                          </div>
                        )}
                      </div>
                      
                      <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-${
                        getPriorityColor(event.priority)
                      }-100 dark:bg-${getPriorityColor(event.priority)}-900/20 text-${
                        getPriorityColor(event.priority)
                      }-700 dark:text-${getPriorityColor(event.priority)}-400 whitespace-nowrap`}>
                        {event.priority === 'high' ? 'Yüksek' : event.priority === 'medium' ? 'Orta' : 'Düşük'}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Day Details Modal Component
  const DayDetailsModal = () => (
    <AnimatePresence>
      {showDayDetailsModal && selectedDayForDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowDayDetailsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative">
              <div className="bg-gradient-to-r from-primary-500 to-blue-600 p-6 text-white">
                <button
                  onClick={() => setShowDayDetailsModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center justify-center w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm">
                    <span className="text-2xl font-bold">
                      {selectedDayForDetails.getDate()}
                    </span>
                    <span className="text-xs opacity-90">
                      {MONTHS[selectedDayForDetails.getMonth()].substring(0, 3)}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold">
                      {selectedDayForDetails.getDate()} {MONTHS[selectedDayForDetails.getMonth()]} {selectedDayForDetails.getFullYear()}
                    </h2>
                    <p className="text-primary-100 mt-1">
                      {(() => {
                        const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
                        return dayNames[selectedDayForDetails.getDay()];
                      })()}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{selectedDayDetailEvents.length} Etkinlik</span>
                      </div>
                      {(() => {
                        const now = new Date();
                        const isToday = selectedDayForDetails.toDateString() === now.toDateString();
                        const isPast = selectedDayForDetails < now && !isToday;
                        const isFuture = selectedDayForDetails > now && !isToday;
                        
                        return (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>
                              {isToday ? 'Bugün' : isPast ? 'Geçmiş' : 'Gelecek'}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(() => {
                    const highPriorityCount = selectedDayDetailEvents.filter(e => e.priority === 'high').length;
                    const completedCount = selectedDayDetailEvents.filter(e => e.status === 'done').length;
                    const activeCount = selectedDayDetailEvents.filter(e => e.status !== 'done' && e.status !== 'cancelled').length;
                    const totalDuration = selectedDayDetailEvents.reduce((total, event) => {
                      return total + (event.end - event.start);
                    }, 0);
                    const hours = Math.round(totalDuration / (1000 * 60 * 60));

                    return (
                      <>
                        <motion.div 
                          className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{highPriorityCount}</div>
                          <div className="text-xs text-red-700 dark:text-red-300">Yüksek Öncelik</div>
                        </motion.div>
                        
                        <motion.div 
                          className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{completedCount}</div>
                          <div className="text-xs text-green-700 dark:text-green-300">Tamamlandı</div>
                        </motion.div>
                        
                        <motion.div 
                          className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{activeCount}</div>
                          <div className="text-xs text-blue-700 dark:text-blue-300">Aktif</div>
                        </motion.div>
                        
                        <motion.div 
                          className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                            {formatDuration(totalDuration)}
                          </div>
                          <div className="text-xs text-purple-700 dark:text-purple-300">Toplam Süre</div>
                        </motion.div>
                      </>
                    );
                  })()}
                </div>

                {/* Timeline View */}
                {selectedDayDetailEvents.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-primary-500" />
                      Günün Programı
                    </h3>
                    
                    <div className="space-y-3">
                      {selectedDayDetailEvents
                        .sort((a, b) => a.start - b.start)
                        .map((event, index) => (
                          <motion.div
                            key={event.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowDayDetailsModal(false);
                              setShowEventModal(true);
                            }}
                          >
                            {/* Time indicator */}
                            <div className="flex-none">
                              <div className="flex flex-col items-center">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatTime(event.start)}
                                </div>
                                <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 my-1"></div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatTime(event.end)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Event details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div className={`w-3 h-3 rounded-full bg-${getPriorityColor(event.priority)}-500`}></div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {event.title}
                                </h4>
                                
                                {/* Başlıyor/Bitiyor indikatörleri */}
                                {(() => {
                                  const selectedDate = new Date(selectedDayForDetails);
                                  selectedDate.setHours(0, 0, 0, 0);
                                  
                                  const eventStartDate = new Date(event.start);
                                  eventStartDate.setHours(0, 0, 0, 0);
                                  
                                  const eventEndDate = new Date(event.end);
                                  eventEndDate.setHours(0, 0, 0, 0);
                                  
                                  const isStartingToday = selectedDate.getTime() === eventStartDate.getTime();
                                  const isEndingToday = selectedDate.getTime() === eventEndDate.getTime();
                                  
                                  return (
                                    <>
                                      {isStartingToday && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                                          Başlıyor
                                        </span>
                                      )}
                                      {isEndingToday && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                                          Bitiyor
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                                
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${
                                  getPriorityColor(event.priority)
                                }-100 dark:bg-${getPriorityColor(event.priority)}-900/20 text-${
                                  getPriorityColor(event.priority)
                                }-700 dark:text-${getPriorityColor(event.priority)}-400`}>
                                  {event.priority === 'high' ? 'Yüksek' : event.priority === 'medium' ? 'Orta' : 'Düşük'}
                                </span>
                              </div>
                              
                              {event.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                                  {event.description}
                                </p>
                              )}
                              
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                {event.project && (
                                  <div className="flex items-center gap-1">
                                    <Tag className="w-3 h-3" />
                                    <span>{event.project.name}</span>
                                  </div>
                                )}
                                
                                {event.status && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>
                                      {event.status === 'done' ? 'Tamamlandı' : 
                                       event.status === 'todo' ? 'Yapılacak' : 
                                       event.status === 'in_progress' ? 'Devam Ediyor' : 
                                       event.status}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>
                                    {formatDuration(event.end - event.start)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-none" />
                          </motion.div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Bu günde etkinlik yok
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                      Bu gün için henüz planlanmış bir görev veya etkinlik bulunmuyor.
                    </p>
                  </motion.div>
                )}

                {/* Day insights */}
                {selectedDayDetailEvents.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <LayoutGrid className="w-5 h-5 mr-2 text-primary-500" />
                      Gün Analizi
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Project distribution */}
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Proje Dağılımı
                        </h4>
                        {(() => {
                          const projectCounts = {};
                          selectedDayDetailEvents.forEach(event => {
                            if (event.project) {
                              const projectName = event.project.name;
                              projectCounts[projectName] = (projectCounts[projectName] || 0) + 1;
                            }
                          });
                          
                          const projects = Object.entries(projectCounts);
                          
                          if (projects.length === 0) {
                            return <p className="text-xs text-gray-500 dark:text-gray-400">Proje bilgisi yok</p>;
                          }
                          
                          return (
                            <div className="space-y-2">
                              {projects.map(([project, count], index) => (
                                <div key={project} className="flex justify-between items-center">
                                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{project}</span>
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">{count}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Time distribution */}
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Zaman Dağılımı
                        </h4>
                        {(() => {
                          const morningCount = selectedDayDetailEvents.filter(e => e.start.getHours() < 12).length;
                          const afternoonCount = selectedDayDetailEvents.filter(e => e.start.getHours() >= 12 && e.start.getHours() < 18).length;
                          const eveningCount = selectedDayDetailEvents.filter(e => e.start.getHours() >= 18).length;
                          
                          return (
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Sabah (06:00-12:00)</span>
                                <span className="text-xs font-medium text-gray-900 dark:text-white">{morningCount}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Öğleden Sonra (12:00-18:00)</span>
                                <span className="text-xs font-medium text-gray-900 dark:text-white">{afternoonCount}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Akşam (18:00-24:00)</span>
                                <span className="text-xs font-medium text-gray-900 dark:text-white">{eveningCount}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 px-6 py-4">
              <div className="flex justify-between items-center">
                <button
                  onClick={() => {
                    setCurrentDate(new Date(selectedDayForDetails.getFullYear(), selectedDayForDetails.getMonth(), 1));
                    setShowDayDetailsModal(false);
                  }}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Takvime Geri Dön
                </button>
                
                <div className="flex gap-2">
                  {(() => {
                    const prevDay = new Date(selectedDayForDetails);
                    prevDay.setDate(prevDay.getDate() - 1);
                    const nextDay = new Date(selectedDayForDetails);
                    nextDay.setDate(nextDay.getDate() + 1);
                    
                    return (
                      <>
                        <button
                          onClick={() => handleDayClick(prevDay)}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Önceki Gün
                        </button>
                        <button
                          onClick={() => handleDayClick(nextDay)}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
                        >
                          Sonraki Gün
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Weekly Detail Modal
  const WeeklyDetailModal = () => (
    <AnimatePresence>
      {showWeeklyDetailModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowWeeklyDetailModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-tl-[24px] rounded-tr-0 rounded-bl-[24px] rounded-br-0  p-6 max-w-7xl w-full max-h-[95vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.05), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
              filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.15))'
            }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    📊 Gelişmiş Görev Yoğunluğu Analizi
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kapsamlı görev analizi, trend incelemesi ve performans raporları
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowWeeklyDetailModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Date Range Selector */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 mb-6 border border-blue-200 dark:border-blue-800">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
                🎯 Analiz Dönemi Seçimi
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    📅 Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border-2 border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    🏁 Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border-2 border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => {
                      const today = new Date();
                      const oneWeekAgo = new Date(today);
                      oneWeekAgo.setDate(today.getDate() - 7);
                      setCustomDateRange({
                        start: oneWeekAgo.toISOString().split('T')[0],
                        end: today.toISOString().split('T')[0]
                      });
                    }}
                    className="px-4 py-2 text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all transform hover:scale-105 shadow-lg"
                  >
                    📅 Son 7 Gün
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      const oneMonthAgo = new Date(today);
                      oneMonthAgo.setMonth(today.getMonth() - 1);
                      setCustomDateRange({
                        start: oneMonthAgo.toISOString().split('T')[0],
                        end: today.toISOString().split('T')[0]
                      });
                    }}
                    className="px-4 py-2 text-xs bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:from-purple-600 hover:to-violet-600 transition-all transform hover:scale-105 shadow-lg"
                  >
                    📈 Son 30 Gün
                  </button>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      const today = new Date();
                      const threeMonthsAgo = new Date(today);
                      threeMonthsAgo.setMonth(today.getMonth() - 3);
                      setCustomDateRange({
                        start: threeMonthsAgo.toISOString().split('T')[0],
                        end: today.toISOString().split('T')[0]
                      });
                    }}
                    className="px-4 py-2 text-xs bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg"
                  >
                    🚀 Son 3 Ay
                  </button>
                </div>
              </div>
            </div>

            {/* Enhanced Analysis */}
            {(() => {
              if (!customDateRange.start || !customDateRange.end) {
                return (
                  <div className="text-center py-16 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                    <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CalendarIcon className="w-10 h-10 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      🎯 Analiz Başlatmaya Hazır
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 animate-pulse">
                      Detaylı analiz için yukarıdan tarih aralığını seçiniz
                    </p>
                  </div>
                );
              }

              const startDate = new Date(customDateRange.start);
              const endDate = new Date(customDateRange.end);
              
              if (startDate > endDate) {
                return (
                  <div className="text-center py-16 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                    <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                      ⚠️ Tarih Hatası
                    </h3>
                    <p className="text-red-500 dark:text-red-400">
                      Başlangıç tarihi bitiş tarihinden sonra olamaz
                    </p>
                  </div>
                );
              }

              // Enhanced analysis calculations
              const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
              const analysisDays = [];
              const projectStats = {};
              const userStats = {};
              
              // TOPLAM EŞSİZ GÖREV SAYISI için Set kullan
              const uniqueTaskIds = new Set();

              for (let i = 0; i < daysDiff; i++) {
                const currentDay = new Date(startDate);
                currentDay.setDate(startDate.getDate() + i);
                currentDay.setHours(0, 0, 0, 0);

                const taskIds = new Set();
                
                // Task filtering logic
                const startingTasks = events.filter(event => {
                  const eventStart = new Date(event.start);
                  eventStart.setHours(0, 0, 0, 0);
                  const isStarting = eventStart.getTime() === currentDay.getTime();
                  if (isStarting) {
                    taskIds.add(event.id);
                    uniqueTaskIds.add(event.id); // Toplam için ekle
                    // Project stats - sadece başlayan görevleri say
                    if (event.projectName) {
                      projectStats[event.projectName] = (projectStats[event.projectName] || 0) + 1;
                    }
                    // User stats - sadece başlayan görevleri say
                    if (event.assignedTo) {
                      userStats[event.assignedTo] = (userStats[event.assignedTo] || 0) + 1;
                    }
                  }
                  return isStarting;
                });

                const endingTasks = events.filter(event => {
                  const eventEnd = new Date(event.end);
                  eventEnd.setHours(0, 0, 0, 0);
                  const isEnding = eventEnd.getTime() === currentDay.getTime();
                  if (isEnding) {
                    taskIds.add(event.id);
                    uniqueTaskIds.add(event.id); // Toplam için ekle
                  }
                  return isEnding;
                });

                const ongoingTasks = events.filter(event => {
                  const eventStart = new Date(event.start);
                  const eventEnd = new Date(event.end);
                  eventStart.setHours(0, 0, 0, 0);
                  eventEnd.setHours(0, 0, 0, 0);
                  
                  const isOngoing = eventStart.getTime() < currentDay.getTime() && 
                                  eventEnd.getTime() > currentDay.getTime();
                  if (isOngoing) {
                    taskIds.add(event.id);
                    uniqueTaskIds.add(event.id); // Toplam için ekle
                  }
                  return isOngoing;
                });

                const dayEvents = [...startingTasks, ...endingTasks, ...ongoingTasks]
                  .filter((task, index, arr) => arr.findIndex(t => t.id === task.id) === index);

                // Enhanced metrics
                const highPriority = dayEvents.filter(task => task.priority === 'high').length;
                const mediumPriority = dayEvents.filter(task => task.priority === 'medium').length;
                const lowPriority = dayEvents.filter(task => task.priority === 'low').length;
                const completed = dayEvents.filter(task => task.status === 'done').length;
                const inProgress = dayEvents.filter(task => task.status === 'in_progress').length;
                const pending = dayEvents.filter(task => task.status === 'todo' || task.status === 'pending').length;

                analysisDays.push({
                  date: new Date(currentDay),
                  dayName: currentDay.toLocaleDateString('tr-TR', { weekday: 'long' }),
                  totalTasks: taskIds.size,
                  startingTasks: startingTasks.length,
                  endingTasks: endingTasks.length,
                  ongoingTasks: ongoingTasks.length,
                  highPriority,
                  mediumPriority,
                  lowPriority,
                  completed,
                  inProgress,
                  pending,
                  isWeekend: currentDay.getDay() === 0 || currentDay.getDay() === 6,
                  productivity: completed > 0 ? (completed / taskIds.size * 100).toFixed(1) : 0
                });
              }

              // DÜZELTME: Toplam için eşsiz görev sayısını kullan
              const totalTasks = uniqueTaskIds.size; // BURASI DÜZELTİLDİ!
              const maxTasks = Math.max(...analysisDays.map(day => day.totalTasks), 1);
              const avgTasksPerDay = (totalTasks / daysDiff).toFixed(1);
              const busiestDay = analysisDays.reduce((max, day) => day.totalTasks > max.totalTasks ? day : max);
              const quietestDay = analysisDays.reduce((min, day) => day.totalTasks < min.totalTasks ? day : min);
              
              // DÜZELTME: Öncelik ve durum hesaplamaları için eşsiz görevleri kullan
              const allUniqueEvents = events.filter(event => {
                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end);
                eventStart.setHours(0, 0, 0, 0);
                eventEnd.setHours(0, 0, 0, 0);
                
                return (eventStart >= startDate && eventStart <= endDate) ||
                       (eventEnd >= startDate && eventEnd <= endDate) ||
                       (eventStart < startDate && eventEnd > endDate);
              }).filter((task, index, arr) => arr.findIndex(t => t.id === task.id) === index);
              
              const totalCompleted = allUniqueEvents.filter(task => task.status === 'done').length;
              const overallProductivity = totalTasks > 0 ? (totalCompleted / totalTasks * 100).toFixed(1) : 0;

              // Project rankings
              const topProjects = Object.entries(projectStats)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5);

              // User rankings
              const topUsers = Object.entries(userStats)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5);

              return (
                <div className="space-y-8">
                  {/* Enhanced Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{totalTasks}</div>
                      <div className="text-sm opacity-90">📊 Toplam Görev</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{avgTasksPerDay}</div>
                      <div className="text-sm opacity-90">📈 Günlük Ortalama</div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{busiestDay.totalTasks}</div>
                      <div className="text-sm opacity-90">🔥 En Yoğun Gün</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{daysDiff}</div>
                      <div className="text-sm opacity-90">📅 Analiz Günü</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{overallProductivity}%</div>
                      <div className="text-sm opacity-90">✅ Verimlilik</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-xl text-white">
                      <div className="text-2xl font-bold">{Object.keys(projectStats).length}</div>
                      <div className="text-sm opacity-90">🚀 Aktif Proje</div>
                    </div>
                  </div>

                  {/* Advanced Analytics Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Enhanced Daily Distribution */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                      <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                        📊 Günlük Görev Dağılımı
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                          Detaylı
                        </span>
                      </h5>
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {analysisDays.map((day, index) => {
                          // O gün için görevleri tekrar hesapla
                          const dayStartingTasks = events.filter(event => {
                            const eventStart = new Date(event.start);
                            eventStart.setHours(0, 0, 0, 0);
                            return eventStart.getTime() === day.date.getTime();
                          });
                          
                          const dayEndingTasks = events.filter(event => {
                            const eventEnd = new Date(event.end);
                            eventEnd.setHours(0, 0, 0, 0);
                            return eventEnd.getTime() === day.date.getTime();
                          });
                          
                          const dayOngoingTasks = events.filter(event => {
                            const eventStart = new Date(event.start);
                            const eventEnd = new Date(event.end);
                            eventStart.setHours(0, 0, 0, 0);
                            eventEnd.setHours(0, 0, 0, 0);
                            
                            return eventStart.getTime() < day.date.getTime() && 
                                   eventEnd.getTime() > day.date.getTime();
                          });

                          return (
                            <div 
                              key={index} 
                              className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all border border-gray-100 dark:border-gray-600 cursor-pointer"
                              onClick={() => handleAnalysisDayClick(day, dayStartingTasks, dayEndingTasks, dayOngoingTasks)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {day.date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                                  </div>
                                  <div className={`text-xs px-2 py-1 rounded-full ${
                                    day.isWeekend 
                                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' 
                                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  }`}>
                                    {day.dayName}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                                    {day.totalTasks}
                                  </span>
                                  <span className="text-xs text-green-600 dark:text-green-400">
                                    {day.productivity}% ✅
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-2">
                                <div
                                  className={`h-full transition-all duration-500 ${
                                    day.isWeekend 
                                      ? 'bg-gradient-to-r from-purple-400 to-purple-600' 
                                      : 'bg-gradient-to-r from-blue-400 to-blue-600'
                                  }`}
                                  style={{ width: `${(day.totalTasks / maxTasks) * 100}%` }}
                                ></div>
                              </div>
                              {day.totalTasks > 0 && (
                                <div className="flex gap-3 text-xs">
                                  {day.startingTasks > 0 && (
                                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                                      🚀 {day.startingTasks} başlıyor
                                    </span>
                                  )}
                                  {day.endingTasks > 0 && (
                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                      ✅ {day.endingTasks} bitiyor
                                    </span>
                                  )}
                                  {day.ongoingTasks > 0 && (
                                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                      ⏳ {day.ongoingTasks} devam
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Project Performance */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                      <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                        🚀 En Aktif Projeler
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                          Top 5
                        </span>
                      </h5>
                      <div className="space-y-3">
                        {topProjects.length > 0 ? topProjects.map(([project, count], index) => (
                          <div key={project} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              index === 0 ? 'bg-yellow-500' : 
                              index === 1 ? 'bg-gray-400' :
                              index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                                {project}
                              </div>
                              <div className="text-xs text-gray-500">
                                {count} görev
                              </div>
                            </div>
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                              {count}
                            </div>
                          </div>
                        )) : (
                          <div className="text-center text-gray-500 py-8">
                            📂 Proje verisi bulunamadı
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Priority & Status Enhanced Analysis */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Enhanced Priority Distribution */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                      <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                        🎯 Öncelik Dağılımı
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-full">
                          Kritik
                        </span>
                      </h5>
                      <div className="space-y-4">
                        {(() => {
                          const totalHigh = allUniqueEvents.filter(task => task.priority === 'high').length;
                          const totalMedium = allUniqueEvents.filter(task => task.priority === 'medium').length;
                          const totalLow = allUniqueEvents.filter(task => task.priority === 'low').length;
                          const total = totalHigh + totalMedium + totalLow;

                          return (
                            <>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                                    🔴 Yüksek Öncelik
                                    {totalHigh > totalMedium + totalLow && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">⚠️ Dikkat</span>}
                                  </span>
                                  <span className="text-sm font-bold">{totalHigh} ({total > 0 ? ((totalHigh / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-red-200 dark:bg-red-900/30 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalHigh / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400 flex items-center gap-2">
                                    🟡 Orta Öncelik
                                  </span>
                                  <span className="text-sm font-bold">{totalMedium} ({total > 0 ? ((totalMedium / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-yellow-200 dark:bg-yellow-900/30 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalMedium / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                                    🟢 Düşük Öncelik
                                  </span>
                                  <span className="text-sm font-bold">{totalLow} ({total > 0 ? ((totalLow / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-green-200 dark:bg-green-900/30 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalLow / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Enhanced Status Distribution */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                      <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                        📋 Durum Dağılımı
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                          {overallProductivity}% Verim
                        </span>
                      </h5>
                      <div className="space-y-4">
                        {(() => {
                          const totalCompleted = allUniqueEvents.filter(task => task.status === 'done').length;
                          const totalInProgress = allUniqueEvents.filter(task => task.status === 'in_progress').length;
                          const totalPending = allUniqueEvents.filter(task => task.status === 'todo' || task.status === 'pending').length;
                          const total = totalCompleted + totalInProgress + totalPending;

                          return (
                            <>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                                    ✅ Tamamlandı
                                    {totalCompleted > totalInProgress + totalPending && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🎉 Süper</span>}
                                  </span>
                                  <span className="text-sm font-bold">{totalCompleted} ({total > 0 ? ((totalCompleted / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-green-200 dark:bg-green-900/30 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalCompleted / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">
                                    🔄 Devam Ediyor
                                  </span>
                                  <span className="text-sm font-bold">{totalInProgress} ({total > 0 ? ((totalInProgress / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-blue-200 dark:bg-blue-900/30 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalInProgress / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                    ⏳ Bekliyor
                                    {totalPending > totalCompleted && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">⚠️ Yoğun</span>}
                                  </span>
                                  <span className="text-sm font-bold">{totalPending} ({total > 0 ? ((totalPending / total) * 100).toFixed(1) : 0}%)</span>
                                </div>
                                <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-gray-500 to-gray-600 rounded-full transition-all duration-700"
                                    style={{ width: `${total > 0 ? (totalPending / total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Advanced Insights & Recommendations */}
                  <div className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 p-6 rounded-xl border border-primary-200 dark:border-primary-800">
                    <h5 className="text-lg font-semibold text-primary-700 dark:text-primary-300 mb-4 flex items-center gap-2">
                      🧠 Gelişmiş Analiz & Öneriler
                      <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-full">
                        AI Destekli
                      </span>
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-3">
                        <h6 className="font-medium text-gray-800 dark:text-gray-200">📊 Genel Durum</h6>
                        <div className="text-sm space-y-1">
                          <p><strong>📅 En Yoğun:</strong> {busiestDay.date.toLocaleDateString('tr-TR')} ({busiestDay.totalTasks} görev)</p>
                          <p><strong>😌 En Sakin:</strong> {quietestDay.date.toLocaleDateString('tr-TR')} ({quietestDay.totalTasks} görev)</p>
                          <p><strong>📈 Ortalama Yoğunluk:</strong> {avgTasksPerDay} görev/gün</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h6 className="font-medium text-gray-800 dark:text-gray-200">⏰ Zaman Analizi</h6>
                        <div className="text-sm space-y-1">
                          <p><strong>🏖️ Hafta Sonu:</strong> {analysisDays.filter(d => d.isWeekend).reduce((sum, d) => sum + d.totalTasks, 0)} görev</p>
                          <p><strong>💼 Hafta İçi:</strong> {analysisDays.filter(d => !d.isWeekend).reduce((sum, d) => sum + d.totalTasks, 0)} görev</p>
                          <p><strong>⚡ En Verimli Gün:</strong> {analysisDays.reduce((max, day) => parseFloat(day.productivity) > parseFloat(max.productivity) ? day : max).dayName}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h6 className="font-medium text-gray-800 dark:text-gray-200">🎯 Öneriler</h6>
                        <div className="text-sm space-y-1">
                          {overallProductivity < 30 && <p className="text-orange-600">⚠️ Verimlilik artırılabilir</p>}
                          {busiestDay.totalTasks > avgTasksPerDay * 2 && <p className="text-red-600">🚨 Görev dağılımını dengeleyin</p>}
                          {analysisDays.filter(d => d.isWeekend).reduce((sum, d) => sum + d.totalTasks, 0) > analysisDays.filter(d => !d.isWeekend).reduce((sum, d) => sum + d.totalTasks, 0) && 
                           <p className="text-blue-600">🏖️ Hafta sonu yoğunluğu fazla</p>}
                          {overallProductivity > 70 && <p className="text-green-600">🎉 Mükemmel performans!</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Analiz günü detay modalı
  const AnalysisDayDetailModal = () => (
    <AnimatePresence>
      {showDayDetailInAnalysis && selectedAnalysisDay && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowDayDetailInAnalysis(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    📅 {selectedAnalysisDay.date.toLocaleDateString('tr-TR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Günlük görev detayları ve analizi
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDayDetailInAnalysis(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Özet İstatistikler */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl text-white">
                <div className="text-2xl font-bold">{selectedAnalysisDayTasks.starting.length}</div>
                <div className="text-sm opacity-90">🚀 Başlayan Görevler</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl text-white">
                <div className="text-2xl font-bold">{selectedAnalysisDayTasks.ending.length}</div>
                <div className="text-sm opacity-90">✅ Biten Görevler</div>
              </div>
              <div className="bg-gradient-to-br from-yellow-500 to-orange-500 p-4 rounded-xl text-white">
                <div className="text-2xl font-bold">{selectedAnalysisDayTasks.ongoing.length}</div>
                <div className="text-sm opacity-90">⏳ Devam Eden</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-xl text-white">
                <div className="text-2xl font-bold">{selectedAnalysisDay.totalTasks}</div>
                <div className="text-sm opacity-90">📊 Toplam Aktif</div>
              </div>
            </div>

            {/* Görev Listeleri */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Başlayan Görevler */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4 flex items-center gap-2">
                  🚀 Başlayan Görevler
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                    {selectedAnalysisDayTasks.starting.length}
                  </span>
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedAnalysisDayTasks.starting.length > 0 ? (
                    selectedAnalysisDayTasks.starting.map((task, index) => (
                      <div key={index} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-200 dark:border-green-700">
                        <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-1">
                          {task.title}
                        </h5>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {task.priority === 'high' ? 'Yüksek' : task.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </span>
                          <span className="text-gray-500">
                            {formatTime(task.start)}
                          </span>
                        </div>
                        {task.projectName && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            📁 {task.projectName}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      📭 Bu gün başlayan görev yok
                    </div>
                  )}
                </div>
              </div>

              {/* Biten Görevler */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4 flex items-center gap-2">
                  ✅ Biten Görevler
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                    {selectedAnalysisDayTasks.ending.length}
                  </span>
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedAnalysisDayTasks.ending.length > 0 ? (
                    selectedAnalysisDayTasks.ending.map((task, index) => (
                      <div key={index} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
                        <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-1">
                          {task.title}
                        </h5>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            task.status === 'done' ? 'bg-green-100 text-green-700' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {task.status === 'done' ? 'Tamamlandı' : 
                             task.status === 'in_progress' ? 'Devam Ediyor' : 'Beklemede'}
                          </span>
                          <span className="text-gray-500">
                            {formatTime(task.end)}
                          </span>
                        </div>
                        {task.projectName && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            📁 {task.projectName}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      📭 Bu gün biten görev yok
                    </div>
                  )}
                </div>
              </div>

              {/* Devam Eden Görevler */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
                <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-4 flex items-center gap-2">
                  ⏳ Devam Eden Görevler
                  <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded-full">
                    {selectedAnalysisDayTasks.ongoing.length}
                  </span>
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedAnalysisDayTasks.ongoing.length > 0 ? (
                    selectedAnalysisDayTasks.ongoing.map((task, index) => (
                      <div key={index} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-yellow-200 dark:border-yellow-700">
                        <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-1">
                          {task.title}
                        </h5>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {task.priority === 'high' ? 'Yüksek' : task.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </span>
                          <span className="text-gray-500">
                            Devam ediyor
                          </span>
                        </div>
                        {task.projectName && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            📁 {task.projectName}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      📭 Bu gün devam eden görev yok
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Günlük Özet */}
            <div className="mt-6 bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                📈 Günlük Özet
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Verimlilik:</span>
                  <span className="ml-2 font-semibold text-green-600">{selectedAnalysisDay.productivity}%</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Gün Türü:</span>
                  <span className="ml-2 font-semibold">
                    {selectedAnalysisDay.isWeekend ? '🏖️ Hafta Sonu' : '💼 Hafta İçi'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Yüksek Öncelik:</span>
                  <span className="ml-2 font-semibold text-red-600">{selectedAnalysisDay.highPriority}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Tamamlanan:</span>
                  <span className="ml-2 font-semibold text-green-600">{selectedAnalysisDay.completed}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (isLoading) {
    return <CalendarSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mt-2 mb-8">
        {/* Permission level indicator */}
        {userRole && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            userRole === 'super' 
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
              : userRole === 'admin' 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {userRole === 'super' ? (
              <Shield className="w-4 h-4" />
            ) : userRole === 'admin' ? (
              <User className="w-4 h-4" />
            ) : (
              <Users className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {userRole === 'super' 
                ? 'Süper Yönetici' 
                : userRole === 'admin' 
                  ? 'Yönetici' 
                  : 'Standart Kullanıcı'
              }
            </span>
          </div>
        )}
        {/* Super user filter toggle */}
        {userRole === 'super' && (
          <div className="relative mr-2">
            <button
              type="button"
              onClick={() => setShowOnlyMyTasks(!showOnlyMyTasks)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showOnlyMyTasks
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              }`}
            >
              {showOnlyMyTasks ? (
                <Users className="w-4 h-4" />
              ) : (
                <LayoutGrid className="w-4 h-4" />
              )}
              {showOnlyMyTasks ? 'Görevlerim' : 'Tüm Görevler'}
            </button>
          </div>
        )}
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Takvim Başlığı ve Araçlar */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Takvim
              </h2>
            {/*<button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Yeni Etkinlik
              </button>*/}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-200 to-blue-200 dark:from-primary-800 dark:to-blue-800 opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300 rounded-lg -z-10"></div>
                <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 pr-3">
                <input
                  type="text"
                    placeholder="Görev ya da etkinlik ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-64 pl-10 pr-4 py-2.5 text-sm border-0 bg-transparent text-gray-900 dark:text-white focus:outline-none focus:ring-0"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  {searchQuery && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSearchQuery('')}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  )}
                </div>
                
                {/* Arama Sonuçları Dropdown */}
                <AnimatePresence>
                  {showSearchResults && searchResults.length > 0 && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ type: "spring", damping: 20, stiffness: 300 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-10 max-h-80 overflow-y-auto overflow-x-hidden"
                    >
                      <div className="sticky top-0 p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90 z-10">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            <span className="text-primary-600 dark:text-primary-400 font-bold">{searchResults.length}</span> sonuç bulundu
                          </p>
                          <button
                            onClick={() => setShowSearchResults(false)}
                            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {searchResults.map((result, index) => (
                          <motion.div
                            key={result.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            whileHover={{ 
                              backgroundColor: "rgba(59, 130, 246, 0.05)",
                              x: 3
                            }}
                            className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all"
                            onClick={() => selectTaskAndNavigate(result)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-2.5 h-2.5 rounded-full bg-${getPriorityColor(result.priority)}-500 ring-2 ring-${getPriorityColor(result.priority)}-200 dark:ring-${getPriorityColor(result.priority)}-900/50 flex-shrink-0`}></div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                  {result.title}
                                </p>
                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  <Clock className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                  <span className="truncate">{formatDate(result.start)}</span>
                                </div>
                                {result.project && (
                                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    <Tag className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                    <span className="truncate">{result.project.name}</span>
                                  </div>
                                )}
                              </div>
                              <motion.div 
                                whileHover={{ x: 3 }}
                                whileTap={{ scale: 0.9 }}
                                className="ml-2 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </motion.div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      
                      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 text-center">
                        <button
                          onClick={() => {
                            navigate('/tasks');
                            setShowSearchResults(false);
                          }}
                          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          Tüm sonuçları görüntüle
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors relative ${
                  showFilters
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-5 h-5" />
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setView('month')}
                className={`p-2 rounded-lg transition-colors ${
                  view === 'month'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-2 rounded-lg transition-colors ${
                  view === 'list'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <ListIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Gelişmiş Filtreler */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 overflow-hidden"
              >
                <div className="p-5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Filtreler
                    </h3>
                    <button
                      onClick={resetFilters}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Öncelik Filtresi */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Öncelik
                      </label>
                      <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="all">Tümü</option>
                        <option value="high">Yüksek</option>
                        <option value="medium">Orta</option>
                        <option value="low">Düşük</option>
                      </select>
                    </div>
                    
                    {/* Durum Filtresi */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Durum
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="all">Tümü</option>
                        <option value="todo">Yapılacak</option>
                        {/*<option value="in_progress">Devam Ediyor</option>
                        {/*<option value="pending">Beklemede</option>
                        <option value="done">Tamamlandı</option>
                        {/*<option value="cancelled">İptal Edildi</option>*/}
                      </select>
                    </div>
                  </div>
                  
                  {/* Tarih Aralığı Filtresi */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Tarihi
                      </label>
                      <input
                        type="date"
                        value={dateRangeFilter.start}
                        onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                        className="w-full p-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bitiş Tarihi
                      </label>
                      <input
                        type="date"
                        value={dateRangeFilter.end}
                        onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                        min={dateRangeFilter.start}
                        className="w-full p-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Tarihe Göre Sıralama */}
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tarihe Göre Sırala
                    </label>
                    <select
                      value={sortByDate}
                      onChange={(e) => setSortByDate(e.target.value)}
                      className="w-full p-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="default">Varsayılan</option>
                      <option value="newest">En Yeni Tarih</option>
                      <option value="oldest">En Eski Tarih</option>
                    </select>
                  </div>
                  
                  {/* Aktif Filtreler */}
                  {activeFiltersCount > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mr-2 py-1">
                        Aktif Filtreler:
                      </p>
                      
                      {priorityFilter !== 'all' && (
                        <span className={`px-2 py-1 text-xs bg-${getPriorityColor(priorityFilter)}-100 dark:bg-${getPriorityColor(priorityFilter)}-900/30 text-${getPriorityColor(priorityFilter)}-700 dark:text-${getPriorityColor(priorityFilter)}-400 rounded-full flex items-center`}>
                          {priorityFilter === 'high' ? 'Yüksek' : priorityFilter === 'medium' ? 'Orta' : 'Düşük'} Öncelik
                      <button
                            onClick={() => setPriorityFilter('all')}
                            className={`ml-1 hover:text-${getPriorityColor(priorityFilter)}-900 dark:hover:text-${getPriorityColor(priorityFilter)}-200`}
                          >
                            ×
                      </button>
                        </span>
                      )}
                      
                      {statusFilter !== 'all' && (
                        <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center">
                          {statusFilter === 'todo' ? 'Yapılacak' : 
                           statusFilter === 'in_progress' ? 'Devam Ediyor' : 
                           statusFilter === 'pending' ? 'Beklemede' : 
                           statusFilter === 'done' ? 'Tamamlandı' : 
                           statusFilter === 'cancelled' ? 'İptal Edildi' : statusFilter}
                          <button 
                            onClick={() => setStatusFilter('all')}
                            className="ml-1 hover:text-green-900 dark:hover:text-green-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      
                      {(dateRangeFilter.start || dateRangeFilter.end) && (
                        <span className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full flex items-center">
                          {dateRangeFilter.start && dateRangeFilter.end 
                            ? `${new Date(dateRangeFilter.start).toLocaleDateString()} - ${new Date(dateRangeFilter.end).toLocaleDateString()}`
                            : dateRangeFilter.start 
                              ? `>= ${new Date(dateRangeFilter.start).toLocaleDateString()}`
                              : `<= ${new Date(dateRangeFilter.end).toLocaleDateString()}`
                          }
                          <button 
                            onClick={() => setDateRangeFilter({ start: '', end: '' })}
                            className="ml-1 hover:text-orange-900 dark:hover:text-orange-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      
                      {sortByDate !== 'default' && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full flex items-center">
                          {sortByDate === 'newest' ? 'En Yeni Tarih' : 'En Eski Tarih'}
                          <button 
                            onClick={() => setSortByDate('default')}
                            className="ml-1 hover:text-blue-900 dark:hover:text-blue-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      
                      {searchQuery && (
                        <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full flex items-center">
                          "{searchQuery}"
                          <button 
                            onClick={() => setSearchQuery('')}
                            className="ml-1 hover:text-gray-900 dark:hover:text-gray-100"
                          >
                            ×
                          </button>
                        </span>
                      )}
                  </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <motion.button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Önceki Ay"
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.button>
              
              <motion.h3 
                className="text-lg font-medium text-gray-900 dark:text-white px-2"
                key={`${currentDate.getMonth()}-${currentDate.getFullYear()}`}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12 }}
              >
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </motion.h3>
              
              <motion.button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Sonraki Ay"
              >
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Hızlı Gezinme Butonları - Modern ve Minimal Tasarım */}
              <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 shadow-sm">
                <motion.button
                  onClick={() => {
                    const today = new Date();
                    setCurrentDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center justify-center"
                  whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                  whileTap={{ scale: 0.97 }}
                  title="Geçen Ay"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  <span className="text-xs">Önceki</span>
                </motion.button>
                
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>
                
                <motion.button
              onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors flex items-center justify-center"
                  whileHover={{ backgroundColor: "rgba(59, 130, 246, 0.1)" }}
                  whileTap={{ scale: 0.97 }}
                  title="Bugüne Git"
            >
              Bugün
                </motion.button>
                
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>
                
                <motion.button
                  onClick={() => {
                    const today = new Date();
                    setCurrentDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center justify-center"
                  whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                  whileTap={{ scale: 0.97 }}
                  title="Gelecek Ay"
                >
                  <span className="text-xs">Sonraki</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </motion.button>
              </div>
              
              {/* Yıl Seçici */}
              <div className="relative">
                <select
                  value={currentDate.getFullYear()}
                  onChange={(e) => {
                    const year = parseInt(e.target.value);
                    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
                  }}
                  className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400 transform rotate-90" />
                </div>
              </div>
              
              {/* Ay Seçici */}
              <div className="relative">
                <select
                  value={currentDate.getMonth()}
                  onChange={(e) => {
                    const month = parseInt(e.target.value);
                    setCurrentDate(new Date(currentDate.getFullYear(), month, 1));
                  }}
                  className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {MONTHS.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400 transform rotate-90" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {view === 'month' ? (
          // Ay Görünümü
          <div className="p-6">
            {/* Haftanın Günleri */}
            <div className="grid grid-cols-7 mb-4">
              {DAYS.map((day) => (
                <div
                  key={day}
                  className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Takvim Günleri */}
            <div className="grid grid-cols-7 gap-1 bg-gray-50 dark:bg-gray-900/50 rounded-lg overflow-hidden p-1">
              {getDaysInMonth(currentDate).map((day, index) => {
                const events = getEventsForDate(day.date);
                const isToday = day.date.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();
                
                // Tarihin görev aralığı içinde olup olmadığını kontrol et
                const inTaskRange = isDateInTaskRange(day.date);
                
                // Başlangıç veya bitiş günü mü kontrol et
                const taskType = getDateTaskType(day.date);
                
                // Bitiş günü olan görevleri getir
                const endingTasks = getTasksEndingOnDate(day.date);
                
                // Stil sınıflarını belirle
                let dayClassName = `min-h-[110px] bg-white dark:bg-gray-800 rounded-lg p-2 ${
                  !day.isCurrentMonth ? 'opacity-40' : ''
                } ${isSelected ? 'ring-2 ring-primary-500 dark:ring-primary-400' : ''} transition-all duration-300 hover:shadow-md`;
                
                // Başlangıç veya bitiş günü ise özel stiller ekle
                if (taskType.isStart && !taskType.isEnd) {
                  dayClassName += ' border-l-4 border-blue-500 dark:border-blue-600';
                } else if (taskType.isEnd && !taskType.isStart) {
                  dayClassName += ' border-r-4 border-green-500 dark:border-green-600';
                } else if (taskType.isStart && taskType.isEnd) {
                  dayClassName += ' border-x-4 border-purple-500 dark:border-purple-600';
                }

                return (
                  <motion.div
                    key={index}
                    className={`${dayClassName} relative group cursor-pointer`}
                    whileHover={{ y: -2, transition: { duration: 0.2 } }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.005 }}
                    onClick={(e) => {
                      // Görev butonları ve interaktif elementler dışında her yere tıklandığında modal'ı aç
                      const isTaskButton = e.target.closest('button') && e.target.closest('button') !== e.currentTarget;
                      if (!isTaskButton) {
                        handleDayClick(day.date);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDayClick(day.date);
                      }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full text-sm ${
                        isToday
                            ? 'bg-primary-600 text-white font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        whileTap={{ scale: 0.9 }}
                    >
                      {day.date.getDate()}
                      </motion.button>
                      
                      {/* Görev indikatörleri */}
                      <motion.div 
                        className="flex space-x-1"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 15 }}
                      >
                        {taskType.isStart && (
                          <motion.div 
                            className="w-2 h-2 rounded-full bg-blue-500" 
                            title="Başlangıç günü"
                            whileHover={{ scale: 1.5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 10 }}
                          />
                        )}
                        {taskType.isEnd && (
                          <motion.div 
                            className="w-2 h-2 rounded-full bg-green-500" 
                            title="Bitiş günü"
                            whileHover={{ scale: 1.5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 10 }}
                          />
                        )}
                      </motion.div>
                    </div>
                    
                    {/* Günün etkinlikleri */}
                    <div className="mt-1 space-y-1 max-h-[80px] overflow-hidden">
                      {/* Biten görevleri göster */}
                      {endingTasks.length > 0 && (
                        <motion.div 
                          className="text-xs text-green-600 dark:text-green-400 font-medium mt-1 mb-2"
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {endingTasks.length > 1 
                            ? `${endingTasks.length} Görev Bitiş Tarihi` 
                            : `"${endingTasks[0].title}" Bitiş Günü`}
                        </motion.div>
                      )}
                      
                      {events.length > 0 ? (
                        events.slice(0, 2).map((event, idx) => (
                        <motion.button
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                            whileHover={{ scale: 1.02, x: 2 }}
                          whileTap={{ scale: 0.98 }}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ 
                              duration: 0.2, 
                              delay: idx * 0.1,
                              type: "spring",
                              stiffness: 100
                            }}
                            className={`w-full text-left px-1.5 py-1 rounded text-xs bg-gray-50 dark:bg-gray-700/70 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              // Bitiş günü ise yeşil kenarlık ekle
                              endingTasks.some(t => t.id === event.id) ? 'border-l-2 border-green-500' : ''
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <div className={`w-1.5 h-1.5 rounded-full bg-${getPriorityColor(event.priority)}-500`} />
                          <div className="font-medium truncate">{event.title}</div>
                            </div>
                        </motion.button>
                        ))
                      ) : null}
                      
                      {/* Daha fazla etkinlik olduğunu göster */}
                      {events.length > 2 && (
                        <motion.button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowMoreClick(day.date, events);
                          }}
                          className="text-xs text-center w-full py-1 text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded transition-colors"
                          whileHover={{ scale: 1.05, backgroundColor: "rgba(59, 130, 246, 0.1)" }}
                          whileTap={{ scale: 0.95 }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: 0.2 }}
                        >
                          +{events.length - 2} daha göster
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ) : (
          // Liste Görünümü
          <div className="p-6">
            <div className="space-y-2">
              {(() => {
                const filteredEvents = getFilteredEvents();
                
                if (filteredEvents.length === 0) {
                  return (
                    <div className="text-center py-16">
                      <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-300">Etkinlik bulunamadı</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                        Filtreleri değiştirerek veya yeni bir etkinlik ekleyerek başlayabilirsiniz.
                      </p>
                      <div className="mt-6 flex justify-center gap-3">
                        <button
                          type="button"
                          onClick={resetFilters}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Filtreleri Sıfırla
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                        >
                          <Plus className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                          Yeni Etkinlik
                        </button>
                      </div>
                    </div>
                  );
                }
                
                return filteredEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                    whileHover={{ scale: 1.01, y: -2 }}
                    whileTap={{ scale: 0.99 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.4, 
                      delay: index * 0.05,
                      type: "spring",
                      stiffness: 50,
                      damping: 10
                    }}
                    className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => handleEventClick(event)}
                >
                    <div className="flex items-start gap-4">
                      <div className="flex-none flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-gray-50 dark:bg-gray-700 mt-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {new Date(event.start).getDate()}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {MONTHS[new Date(event.start).getMonth()].substring(0, 3)}
                        </span>
                      </div>
                      
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base font-medium text-gray-900 dark:text-white truncate">
                        {event.title}
                      </h4>
                          <div className={`w-2 h-2 rounded-full bg-${getPriorityColor(event.priority)}-500 flex-shrink-0`}></div>
                        </div>
                        
                      {event.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mb-2">
                          {event.description}
                        </p>
                      )}
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Clock className="w-3.5 h-3.5 mr-1" />
                          {formatTime(event.start)} - {formatTime(event.end)}
                        </div>
                          
                          {event.project && (
                            <div className="flex items-center">
                              <Tag className="w-3.5 h-3.5 mr-1" />
                              {event.project.name}
                              {event.subProject && ` / ${event.subProject.name}`}
                          </div>
                        )}
                          </div>
                      </div>
                      
                      <div className="flex-none flex flex-col items-end gap-2">
                      <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${
                            getPriorityColor(event.priority)
                          }-100 dark:bg-${getPriorityColor(event.priority)}-900/20 text-${
                            getPriorityColor(event.priority)
                          }-700 dark:text-${getPriorityColor(event.priority)}-400`}
                        >
                          {event.priority === 'high' ? 'Yüksek' : event.priority === 'medium' ? 'Orta' : 'Düşük'}
                      </span>
                    </div>
                  </div>
                </motion.div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Etkinlik Detay Modalı */}
      <EventModal />
      
      {/* Gün etkinlikleri Modalı */}
      <DayEventsModal />

      {/* Day Details Modal */}
      <DayDetailsModal />

      {/* Weekly Detail Modal */}
      <WeeklyDetailModal />

      {/* Analiz günü detay modalı */}
      <AnalysisDayDetailModal />

      {/* Detaylı Bilgiler Bölümü */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Görev Özeti */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <CheckCircle2 className="w-5 h-5 mr-2 text-primary-500" />
            Görev Durumu
          </h3>
          
          <div className="space-y-5">
            {/* İstatistik Kartları */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-400">Aktif Görevler</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1">
                  {events.filter(e => e.status !== 'done' && e.status !== 'cancelled').length}
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-400">Tamamlanan</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400 mt-1">
                  {events.filter(e => e.status === 'done').length}
                </p>
              </div>
            </div>
            
            {/* Öncelik Dağılımı */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Öncelik Dağılımı
              </h4>
              <div className="flex gap-2 h-4 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                {(() => {
                  const highCount = events.filter(e => e.priority === 'high').length;
                  const mediumCount = events.filter(e => e.priority === 'medium').length;
                  const lowCount = events.filter(e => e.priority === 'low').length;
                  const total = events.length || 1; // Sıfıra bölünmeyi önlemek için
                  
                  return (
                    <>
                      <div 
                        className="bg-red-500 h-full" 
                        style={{ width: `${(highCount / total) * 100}%` }} 
                        title={`Yüksek Öncelik: ${highCount}`}
                      />
                      <div 
                        className="bg-yellow-500 h-full" 
                        style={{ width: `${(mediumCount / total) * 100}%` }} 
                        title={`Orta Öncelik: ${mediumCount}`}
                      />
                      <div 
                        className="bg-green-500 h-full" 
                        style={{ width: `${(lowCount / total) * 100}%` }} 
                        title={`Düşük Öncelik: ${lowCount}`}
                      />
                    </>
                  );
                })()}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Yüksek</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span>Orta</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Düşük</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Yaklaşan Deadlines */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-primary-500" />
            Yaklaşan Son Tarihler
          </h3>
          
          <div className="space-y-4">
            {(() => {
              // Bugün ve sonraki 7 gün içinde bitecek görevleri bul
              const now = new Date();
              const nextWeek = new Date(now);
              nextWeek.setDate(now.getDate() + 7);
              
              const upcomingTasks = events
                .filter(event => {
                  // Bitmiş veya iptal edilmiş görevleri hariç tut
                  if (event.status === 'done' || event.status === 'cancelled') return false;
                  
                  // Bitiş tarihi bugün veya sonraki 7 gün içinde olan görevleri seç
                  return event.end >= now && event.end <= nextWeek;
                })
                .sort((a, b) => a.end - b.end) // Bitiş tarihine göre sırala
                .slice(0, 5); // İlk 5 görevi al
              
              if (upcomingTasks.length === 0) {
                return (
                  <motion.div 
                    className="text-center py-8 text-gray-500 dark:text-gray-400"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 100, delay: 0.1 }}
                    >
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50 animate-pulse" />
                      <p>Yaklaşan son tarih bulunmuyor</p>
                    </motion.div>
                  </motion.div>
                );
              }
              
              return upcomingTasks.map((task, index) => {
                // Bitiş tarihine kalan günü hesapla
                const daysLeft = Math.ceil((task.end - now) / (1000 * 60 * 60 * 24));
                
                // Kalan güne göre renk belirle
                let statusColor = 'green';
                if (daysLeft <= 1) statusColor = 'red';
                else if (daysLeft <= 3) statusColor = 'yellow';
                
                return (
                  <motion.div 
                    key={task.id}
                    className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => handleEventClick(task)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    whileHover={{ 
                      scale: 1.02, 
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" 
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {task.title}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(task.end)}
                        </p>
                      </div>
                      <motion.span 
                        className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${statusColor}-100 dark:bg-${statusColor}-900/30 text-${statusColor}-700 dark:text-${statusColor}-400`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 10, delay: 0.1 + index * 0.08 }}
                      >
                        {daysLeft === 0 ? 'Bugün' : 
                         daysLeft === 1 ? 'Yarın' : 
                         `${daysLeft} gün`}
                      </motion.span>
                    </div>
                  </motion.div>
                );
              });
            })()}
          </div>
        </div>
        
        {/* Takvim İstatistikleri */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <LayoutGrid className="w-5 h-5 mr-2 text-primary-500" />
            Takvim Özeti
          </h3>
          
          <div className="space-y-6">
            {/* Aylık İstatistikler */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Bu Ayki Etkinlikler
              </h4>
              
              {(() => {
                const today = new Date();
                const currentMonth = today.getMonth();
                const currentYear = today.getFullYear();
                
                // Bu aydaki etkinlikleri filtrele
                const thisMonthEvents = events.filter(event => {
                  const eventMonth = event.start.getMonth();
                  const eventYear = event.start.getFullYear();
                  return eventMonth === currentMonth && eventYear === currentYear;
                });
                
                // Etkinlik türlerini say
                const taskEvents = thisMonthEvents.filter(e => e.type === 'task').length;
                const meetingEvents = thisMonthEvents.filter(e => e.type === 'meeting').length;
                const deadlineEvents = thisMonthEvents.filter(e => e.type === 'deadline').length;
                const otherEvents = thisMonthEvents.length - taskEvents - meetingEvents - deadlineEvents;
                
                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{thisMonthEvents.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Toplam</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{taskEvents}</p>
                        <p className="text-xs text-purple-700 dark:text-purple-400">Görev</p>
                      </div>
                    </div>
                    
                    <div className="h-5 rounded-full w-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex">
                      {taskEvents > 0 && (
                        <div 
                          className="h-full bg-purple-500"
                          style={{ width: `${(taskEvents / thisMonthEvents.length) * 100}%` }}
                        ></div>
                      )}
                      {meetingEvents > 0 && (
                        <div 
                          className="h-full bg-blue-500"
                          style={{ width: `${(meetingEvents / thisMonthEvents.length) * 100}%` }}
                        ></div>
                      )}
                      {deadlineEvents > 0 && (
                        <div 
                          className="h-full bg-red-500"
                          style={{ width: `${(deadlineEvents / thisMonthEvents.length) * 100}%` }}
                        ></div>
                      )}
                      {otherEvents > 0 && (
                        <div 
                          className="h-full bg-gray-500"
                          style={{ width: `${(otherEvents / thisMonthEvents.length) * 100}%` }}
                        ></div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            
            {/* Haftalık Yoğunluk */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary-500" />
                Haftalık Görev Yoğunluğu
                </span>
                <button
                  onClick={() => setShowWeeklyDetailModal(true)}
                  className="text-xs font-normal text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <ExternalLink className="w-3 h-3" />
                  Detaylı Görüntüle
                </button>
              </h4>
              
              {(() => {
                // Bugünün tarihini al
                const today = new Date();
                
                // Bu haftanın pazartesi gününü bul
                const monday = new Date(today);
                const dayOfWeek = today.getDay();
                const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                monday.setDate(today.getDate() - daysToMonday);
                monday.setHours(0, 0, 0, 0);
                
                // Bu haftanın her günü için detaylı yoğunluk hesapla
                const weekDays = Array.from({ length: 7 }, (_, i) => {
                  const currentDay = new Date(monday);
                  currentDay.setDate(monday.getDate() + i);
                  
                  // O günde başlayan görevler
                  const startingTasks = events.filter(event => {
                    const eventStart = new Date(event.start);
                    eventStart.setHours(0, 0, 0, 0);
                    return eventStart.getTime() === currentDay.getTime();
                  });
                  
                  // O günde biten görevler 
                  const endingTasks = events.filter(event => {
                    const eventEnd = new Date(event.end);
                    eventEnd.setHours(0, 0, 0, 0);
                    return eventEnd.getTime() === currentDay.getTime();
                  });
                  
                  // O günde devam eden görevler
                  const ongoingTasks = events.filter(event => {
                    const eventStart = new Date(event.start);
                    eventStart.setHours(0, 0, 0, 0);
                    const eventEnd = new Date(event.end);
                    eventEnd.setHours(0, 0, 0, 0);
                    
                    return eventStart.getTime() <= currentDay.getTime() && 
                           eventEnd.getTime() >= currentDay.getTime();
                  });
                  
                  // Öncelik seviyelerine göre görevleri grupla
                  const highPriorityTasks = ongoingTasks.filter(task => task.priority === 'high');
                  const mediumPriorityTasks = ongoingTasks.filter(task => task.priority === 'medium');
                  const lowPriorityTasks = ongoingTasks.filter(task => task.priority === 'low');
                  
                  // Duruma göre görevleri grupla
                  const completedTasks = ongoingTasks.filter(task => task.status === 'done');
                  const inProgressTasks = ongoingTasks.filter(task => task.status === 'in_progress');
                  const pendingTasks = ongoingTasks.filter(task => task.status === 'todo' || task.status === 'pending');
                  
                  // Toplam yoğunluk
                  const allTaskIds = new Set([
                    ...startingTasks.map(t => t.id),
                    ...endingTasks.map(t => t.id),
                    ...ongoingTasks.map(t => t.id)
                  ]);
                  
                  // O günün toplam çalışma süresini hesapla
                  const totalDuration = ongoingTasks.reduce((total, task) => {
                    return total + (task.end - task.start);
                  }, 0);
                  
                  return {
                    date: currentDay,
                    dayName: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'][i],
                    shortName: ['P', 'S', 'Ç', 'P', 'C', 'C', 'P'][i],
                    taskCount: allTaskIds.size,
                    startingCount: startingTasks.length,
                    endingCount: endingTasks.length,
                    ongoingCount: ongoingTasks.length,
                    highPriority: highPriorityTasks.length,
                    mediumPriority: mediumPriorityTasks.length,
                    lowPriority: lowPriorityTasks.length,
                    completed: completedTasks.length,
                    inProgress: inProgressTasks.length,
                    pending: pendingTasks.length,
                    totalDuration: totalDuration,
                    isToday: currentDay.toDateString() === today.toDateString(),
                    isPast: currentDay < today,
                    isFuture: currentDay > today,
                    isWeekend: i === 5 || i === 6 // Cumartesi veya Pazar
                  };
                });
                
                const maxTasks = Math.max(...weekDays.map(day => day.taskCount), 1);
                const totalWeekTasks = weekDays.reduce((sum, day) => sum + day.taskCount, 0);
                const avgTasksPerDay = (totalWeekTasks / 7).toFixed(1);
                const busiestDay = weekDays.reduce((max, day) => day.taskCount > max.taskCount ? day : max);
                const quietestDay = weekDays.reduce((min, day) => day.taskCount < min.taskCount ? day : min);
                  
                  return (
                  <div className="space-y-4">
                    {/* Ana yoğunluk çubuğu */}
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                      <div className="grid grid-cols-7 gap-2 h-12 mb-3">
                        {weekDays.map((day, index) => {
                          const heightPercent = (day.taskCount / maxTasks) * 100;
                          
                          return (
                            <div key={index} className="flex flex-col items-center justify-end group relative">
                              {/* Tooltip */}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                                  <div className="font-medium">{day.dayName}</div>
                                  <div className="text-gray-300 dark:text-gray-400">
                                    {day.date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                    </div>
                                  <div className="mt-1 space-y-1">
                                    <div>📊 Toplam: {day.taskCount} görev</div>
                                    {day.startingCount > 0 && <div>🚀 Başlayan: {day.startingCount}</div>}
                                    {day.endingCount > 0 && <div>✅ Biten: {day.endingCount}</div>}
                                    {day.ongoingCount > 0 && <div>⏳ Devam eden: {day.ongoingCount}</div>}
                                    {day.totalDuration > 0 && (
                                      <div>⏱️ Süre: {formatDuration(day.totalDuration)}</div>
                                    )}
                                  </div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
            
                              {/* Yoğunluk çubuğu */}
                              <div 
                                className={`w-full rounded-t-sm transition-all duration-300 hover:scale-105 ${
                                  day.isToday 
                                    ? 'bg-gradient-to-t from-primary-600 to-primary-400 shadow-md' 
                                    : day.taskCount > 0 
                                      ? day.isWeekend
                                        ? 'bg-gradient-to-t from-purple-500 to-purple-400'
                                        : 'bg-gradient-to-t from-blue-500 to-blue-400'
                                      : 'bg-gray-200 dark:bg-gray-600'
                                }`}
                                style={{ height: `${Math.max(heightPercent, 8)}%` }}
                              ></div>
                              
                              {/* Gün adı */}
                              <div className={`text-xs mt-1 font-medium ${
                                day.isToday 
                                  ? 'text-primary-600 dark:text-primary-400' 
                                  : day.isWeekend
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : 'text-gray-600 dark:text-gray-400'
                              }`}>
                                {day.shortName}
                              </div>
                              
                              {/* Görev sayısı */}
                              {day.taskCount > 0 && (
                                <div className={`text-xs font-bold ${
                                  day.isToday 
                                    ? 'text-primary-700 dark:text-primary-300' 
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  {day.taskCount}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Hafta bilgisi */}
                      <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                        Bu hafta ({(() => {
                          const sunday = new Date(monday);
                          sunday.setDate(monday.getDate() + 6);
                          return `${monday.getDate()}-${sunday.getDate()} ${
                            ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                             'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'][monday.getMonth()]
                          }`;
                        })()})
                      </div>
                    </div>
                    
                    {/* Detaylı istatistikler */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {/* Sol kolon */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">📈 Toplam Görev:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{totalWeekTasks}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">📊 Günlük Ortalama:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{avgTasksPerDay}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">🔥 En Yoğun:</span>
                          <span className="font-medium text-blue-600 dark:text-blue-400">
                            {busiestDay.shortName} ({busiestDay.taskCount})
                    </span>
                        </div>
                      </div>
                      
                      {/* Sağ kolon */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">😌 En Sakin:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {quietestDay.shortName} ({quietestDay.taskCount})
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">🎯 Hafta Sonu:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">
                            {weekDays[5].taskCount + weekDays[6].taskCount} görev
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">💼 Hafta İçi:</span>
                          <span className="font-medium text-blue-600 dark:text-blue-400">
                            {weekDays.slice(0, 5).reduce((sum, day) => sum + day.taskCount, 0)} görev
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Öncelik dağılımı */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        🎯 Öncelik Dağılımı (Bu Hafta)
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                          <div className="w-full h-2 bg-red-200 dark:bg-red-900/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500 transition-all duration-300"
                              style={{ 
                                width: `${totalWeekTasks > 0 ? (weekDays.reduce((sum, day) => sum + day.highPriority, 0) / totalWeekTasks) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <div className="mt-1 text-red-600 dark:text-red-400 font-medium">
                            {weekDays.reduce((sum, day) => sum + day.highPriority, 0)} Yüksek
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="w-full h-2 bg-yellow-200 dark:bg-yellow-900/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-yellow-500 transition-all duration-300"
                              style={{ 
                                width: `${totalWeekTasks > 0 ? (weekDays.reduce((sum, day) => sum + day.mediumPriority, 0) / totalWeekTasks) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <div className="mt-1 text-yellow-600 dark:text-yellow-400 font-medium">
                            {weekDays.reduce((sum, day) => sum + day.mediumPriority, 0)} Orta
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="w-full h-2 bg-green-200 dark:bg-green-900/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 transition-all duration-300"
                              style={{ 
                                width: `${totalWeekTasks > 0 ? (weekDays.reduce((sum, day) => sum + day.lowPriority, 0) / totalWeekTasks) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <div className="mt-1 text-green-600 dark:text-green-400 font-medium">
                            {weekDays.reduce((sum, day) => sum + day.lowPriority, 0)} Düşük
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
              );
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar; 